<template>
<div class="sign-in-container">
  <div v-if="siteId && site" class="logged-in-as w-auto d-flex justify-content-center align-items-center">
    <span>{{ site.name }}</span>
  </div>
  <b-row v-if="!siteId && !site" class="mt-4">
    <b-col>
    <div class="instruction" role="instruction">
      You are not signed into any sites. Please select a site to begin.
    </div>
    </b-col>
  </b-row>
  <div v-if="!loading && !siteId && !site" class="mt-4">
    <div class="dropdown w-100">
      <vue-typeahead-bootstrap
        class="mb-4"
        v-model="siteSearch"
        :data="eligibleSites"
        :serializer="site => site.text"
        @hit="selectSite($event)"
        :minMatchingChars="minChars"
        :showOnFocus="true"
        placeholder="Select a site to sign in to"
      />
    </div>
    <b-button variant="success" v-if="!siteId && !site" @click="findClosestSite" class="bg-success text-white find-closest-site mb-4 display-flex align-items-center justify-content-center">
      {{ findBtnText }}
      <font-awesome-icon icon="map-marker-alt" class="icon"/>
    </b-button>
    <div v-if="showGeoLocationAlertMessage" class="alert alert-warning alert-geolocation" role="alert">
      <p>Location services appears to be turned off or blocked.</p>
      <p>Ensure Location services are available and try again.</p>
    </div>
  </div>
  <b-form>
    <b-row v-if="siteId && site">
      <CommonSignIn :site="site" v-on:clear-site="clearSite" v-on:complete="signIn" />
    </b-row>
  </b-form>
</div>
</template>

<script>
import { signIn } from '@/lib/api';
import { EventBus } from '@/lib/event-bus';
import { getStorageItem, clearStorageItem } from '@/lib/local-storage';
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap';
import CommonSignIn from '@/components/siteParts/CommonSignIn.vue';

export default {
  name: 'SignIn',
  components: {
    CommonSignIn,
    VueTypeaheadBootstrap,
  },
  props: {
    sitesRaw: Array,
    myUser: Object,
  },
  data() {
    return {
      minChars: 0,
      gettingLocation: false,
      loading: true,
      location: null,
      siteSearch: '',
      selectedSite: null,
      siteId: null,
      siteName: '',
      findBtnText: 'Find Closest Site',
      showGeoLocationAlertMessage: false,
    };
  },
  computed: {
    eligibleSites() {
      // do not display sites already signed into
      if (!this.myUser || !this.myUser.emailAddress || !this.sitesRaw || !this.sitesRaw.length) return [];
      const signedInto = this.myUser.todaysSignIns
        .filter((signin) => !signin.signOutTime)
        .map((signin) => signin.siteId);
      return this.sitesRaw
        .filter((site) => signedInto.indexOf(site.id) === -1)
        .map((site) => ({ value: site.id, text: `${site.jobNumber} - ${site.name}` }));
    },
    keyedSites() {
      return this.sitesRaw.reduce((accumulator, site) => {
        /* eslint-disable no-param-reassign */
        accumulator[site.id] = site;
        return accumulator;
        /* eslint-enable no-param-reassign */
      }, {});
    },
    site() {
      if (!this.siteId) return null;
      const site = this.keyedSites[this.siteId];
      return site;
    },
  },
  async mounted() {
    await this.load();
    const selectedSiteId = getStorageItem('lastSiteId');
    clearStorageItem('lastSiteId');
    const selectedSite = this.eligibleSites.find((site) => site.value === selectedSiteId);
    if (selectedSite) {
      this.siteId = selectedSite.value;
      this.siteName = selectedSite.text;
    }
    this.showGeoLocationAlertMessage = !navigator.geolocation;
  },
  methods: {
    selectSite(selectedSite) {
      if (selectedSite) {
        this.siteId = selectedSite.value;
        this.siteName = selectedSite.text;
        EventBus.$emit('isCompact', true);
      } else {
        this.clearSite();
      }
    },
    clearSite() {
      this.siteId = null;
      this.siteName = '';
      this.siteSearch = '';
      EventBus.$emit('isCompact', false);
    },
    filterEligibleSites(input) {
      if (input.length === 0) {
        return this.eligibleSites;
      }

      return this.eligibleSites.filter((site) => site.text.toLowerCase().indexOf(input.toLowerCase()));
    },
    async signIn(responses) {
      EventBus.$emit('showLoader');
      let latLng;
      const currentLocation = await new Promise((resolve) => navigator.geolocation.getCurrentPosition((position) => resolve(position), () => resolve(null)));
      if (currentLocation && currentLocation.coords) {
        latLng = `${currentLocation.coords.latitude}, ${currentLocation.coords.longitude}`;
      }
      await signIn(this.site.id, JSON.stringify(responses), latLng);
      EventBus.$emit('triggerAlert', {
        type: 'success',
        message: 'Signed in',
      });
      setTimeout(this.$router.go, 2000);
    },
    async findClosestSite() {
      const originalBtnText = this.findBtnText;
      this.findBtnText = 'Finding closest site...';
      this.showGeoLocationAlertMessage = false;
      // get our location

      if (navigator.geolocation) {
        this.gettingLocation = true;
        this.location = await new Promise((resolve) => navigator.geolocation.getCurrentPosition((position) => resolve(position), () => resolve(null)));
        this.gettingLocation = false;
      }

      // now get the nearest site
      if (!this.location) {
        this.findBtnText = originalBtnText;
        this.showGeoLocationAlertMessage = true;
        return;
      }

      const myLatitude = this.location.coords.latitude;
      const myLongitude = this.location.coords.longitude;
      const that = this;

      // find nearest site
      const nearestSite = this.sitesRaw.reduce((accumulator, site, index) => {
        if (!site.latLng) return accumulator;
        const siteLatitude = site.latLng.substring(0, site.latLng.indexOf(',')).trim();
        const siteLongitude = site.latLng.substring(site.latLng.indexOf(',') + 1).trim();
        // pythagoras is a good assumption
        const distanceToSite = Math.sqrt((myLatitude - siteLatitude) ** 2 + (myLongitude - siteLongitude) ** 2);
        that.sitesRaw[index].distanceToSite = distanceToSite;
        // return this or original
        if (distanceToSite < accumulator.distance) {
          return {
            siteId: site.id,
            distance: distanceToSite,
            value: site.id,
            text: site.name,
          };
        }
        return accumulator;
      }, {
        siteId: null,
        distance: 10000,
        value: null,
        text: null,
      });

      this.selectSite(nearestSite);
      this.findBtnText = originalBtnText;
    },
    async load() {
      EventBus.$emit('showLoader');
      this.loading = true;
      // order the sites
      this.sitesRaw.sort((a, b) => a.name - b.name);
      this.loading = false;
      EventBus.$emit('hideLoader');
    },
  },
};
</script>

<style>
.btn.find-closest-site {
  margin: auto;
  display: block;
  white-space: nowrap;
}
</style>
